/**
 * Configs retrieved by fetch-uuid-redirect-data
 *
 * Config values used by click and finish events:
 * - category
 * - trackedQueryParams
 *
 * Config used by GTM in container.jsx
 * https://github.com/NerdWallet/interstitial-client/blob/master/client/routes/container.jsx#L40
 * https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/121218111/Google+Tag+Manager+GTM#GoogleTagManager(GTM)-Variablescurrentlyexposed
 * - quantcastLabel
 * - gtmPageVertical
 */
import PRODUCT_TYPES from './marketplace-product-types';

// The key is the Marketplace product_type_name
export const VERTICAL_CONFIGS = {
  [PRODUCT_TYPES.AUTO_LOANS]: {
    productType: PRODUCT_TYPES.AUTO_LOANS,
  },
  [PRODUCT_TYPES.BANKING]: {
    category: 'Banking Rate',
    quantcastLabel: 'Vertical.Banking.Clicks',
    gtmEvent: 'conversion_banking',
    productType: PRODUCT_TYPES.BANKING,
  },
  [PRODUCT_TYPES.CREDIT_CARDS]: {
    category: 'Credit Card',
    gtmPageVertical: 'Credit Cards',
    quantcastLabel: 'Vertical.Credit Cards.Clicks',
    gtmEvent: 'conversion_cc',
    productType: PRODUCT_TYPES.CREDIT_CARDS,
    queryParams: [
      'creditScoreFilter',
      'feesFilter',
      'yearsWithCardFilter',
      'monthlySpendFilter',
      'cardNetworkFilter',
      'financialInstitutionsFilter',
      'annualFee',
      'issuer',
      'balanceTransferFee',
      'creditCardRank',
      'categoryRank',
    ],
  },
  [PRODUCT_TYPES.EDUCATION_LOANS]: {
    category: 'EDU Offers',
    gtmPageTopic: 'Student Loans',
    gtmPageVertical: 'Loans',
    quantcastLabel: 'Vertical.EDU.Clicks',
    productType: PRODUCT_TYPES.EDUCATION_LOANS,
    trackedQueryParams: [
      'annualIncomeFilter',
      'borrowerEmploymentStatus',
      'borrowerResidency',
      'borrowerZip',
      'creditScoreFilterMax',
      'creditScoreFilterMin',
      'highestDegreeFilter',
      'highestSchoolAttendedFilter',
      'loanAmountFilter',
      'loanApr',
      'loanMonthlyPayment',
      'loanTerm',
      'pqid',
      'prequalEventType',
      'prequalRequestId',
      'productId',
      'stateFilter',
    ],
  },
  [PRODUCT_TYPES.ESTATE_PLANNING]: {
    productType: PRODUCT_TYPES.ESTATE_PLANNING,
  },
  [PRODUCT_TYPES.EXPANSION]: {
    productType: PRODUCT_TYPES.EXPANSION,
  },
  [PRODUCT_TYPES.FTHB_PROGRAMS]: {
    productType: PRODUCT_TYPES.FTHB_PROGRAMS,
  },
  [PRODUCT_TYPES.INSURANCE]: {
    gtmPageVertical: 'Insurance',
    quantcastLabel: 'Vertical.Insurance.Clicks',
    productType: PRODUCT_TYPES.INSURANCE,
  },
  [PRODUCT_TYPES.INVESTING]: {
    gtmPageVertical: 'Investing',
    category: 'Investing Offers',
    quantcastLabel: 'Vertical.Investing.Clicks',
    eventFields: [
      'institutionName',
      'productName',
      'productType',
      'offerName',
      'offerAccountType',
      'offerReviewType',
    ],
    specialDisclaimers: {
      wealthfront: 'wealthfront',
      'personal-capital-2': 'personalcapital',
      'marcus-invest': 'marcusinvest',
      'merrill-edge': 'merrilledge',
      'merrill-edge-ira': 'merrilledgeira',
      'fidelity-go': 'fidelitygo',
      'stash-invest': 'stash',
    },
    productType: PRODUCT_TYPES.INVESTING,
  },
  [PRODUCT_TYPES.MORTGAGES]: {
    // category is to support legacy GENERIC_CLICK_EVENT
    category: 'Mortgages',
    gtmPageVertical: 'Mortgages',
    productType: PRODUCT_TYPES.MORTGAGES,
  },
  [PRODUCT_TYPES.MOVING]: {
    productType: PRODUCT_TYPES.MOVING,
  },
  [PRODUCT_TYPES.PERSONAL_LOANS]: {
    category: 'Personal Loans Offers',
    gtmPageVertical: 'Loans',
    gtmPageTopic: 'Personal Loans',
    quantcastLabel: 'Vertical.Loans.Clicks',
    productType: PRODUCT_TYPES.PERSONAL_LOANS,
    trackedQueryParams: [
      'annualIncomeFilter',
      'borrowerAge',
      'borrowerEmploymentStatus',
      'borrowerResidency',
      'borrowerZip',
      'creditScoreFilterMax',
      'creditScoreFilterMin',
      'loanAmountFilter',
      'loanTermFilter',
      'loanTermFilter_12',
      'loanTermFilter_24',
      'loanTermFilter_30',
      'loanTermFilter_36',
      'loanTermFilter_48',
      'loanTermFilter_60',
      'loanTermFilter_72',
      'loanTermFilter_84',
      'loanUseFilter',
      'pqid',
      'sortColumn',
      'stateFilter',
      'termsAgree',
    ],
  },
  [PRODUCT_TYPES.SMB_LOANS]: {
    category: 'SMB Offers',
    gtmPageVertical: null, // legacy Redirect Service behavior, check with product team
    quantcastLabel: 'Vertical.Small Business.Clicks',
    productType: PRODUCT_TYPES.SMB_LOANS,
    trackedQueryParams: [
      'annualRevenue',
      'B2BFilter',
      'bankruptcyFilter',
      'borrowerAge',
      'businessAgeFilterMax',
      'businessAgeFilterMin',
      'businessAgeFilterScoreValue',
      'businessIndustry',
      'businessLegalStructure',
      'businessStartDate',
      'creditScoreFilterMax',
      'creditScoreFilterMin',
      'creditScoreFilterScoreValue',
      'invoicesFilter',
      'loanAmountFilter',
      'loanCollateralFilter',
      'loanTermFilterMax',
      'loanTermFilterMin',
      'loanUrgencyFilterMax',
      'loanUrgencyFilterMin',
      'loanUseFilter',
      'monthlyAccountBalance',
      'monthlyRevenueFilterMin',
      'monthlyRevenueFilterScoreValue',
      'monthyRevenueFilterMax',
      'numberofEmployees',
      'ownershipPercentage',
      'percentRevenueCreditCards',
      'personalAnnualIncome',
      'personalGuaranteeFilter',
      'pqid',
      'profitabilityFilter',
      'qualificationScore',
      'sortColumn',
      'stateFilter',
      'termsAgree',
    ],
  },
  [PRODUCT_TYPES.SOFTWARE]: {
    category: 'SMB Offers',
    productType: PRODUCT_TYPES.SOFTWARE,
  },
  [PRODUCT_TYPES.TAXES]: {
    productType: PRODUCT_TYPES.TAXES,
  },
  [PRODUCT_TYPES.UTILITIES]: {
    productType: PRODUCT_TYPES.UTILITIES,
  },
};

const getInsuranceConfigs = (entityType, subCategory) => {
  const config = VERTICAL_CONFIGS[PRODUCT_TYPES.INSURANCE];
  if (entityType === 'institutions') {
    config.category = 'Insurance (Provider)'; // legacy reasons
  } else if (entityType === 'offers') {
    config.category = 'Insurance (Product)'; // legacy reasons
    if (subCategory === 'car') {
      config.gtmPageTopic = 'Auto';
    } else if (subCategory === 'life') {
      config.gtmPageTopic = 'Life Insurance';
    } else if (subCategory === 'homeowners-property') {
      config.gtmPageTopic = 'Home Insurance';
    }
  }
  return config;
};

export const getVerticalConfig = (
  marketplaceProductType,
  marketplaceEntityType,
  subCategory
) => {
  if (marketplaceProductType === PRODUCT_TYPES.INSURANCE) {
    return getInsuranceConfigs(marketplaceEntityType, subCategory);
  }
  return VERTICAL_CONFIGS[marketplaceProductType] || {};
};
